import { useMemo } from 'react';

import { DateRange } from 'react-day-picker';

import useAnalytics, { EMetric } from 'api/useAnalytics';

import {
  ANALYTICS,
  BUSINESS_ANALYTICS,
  BUSINESS_BREAKDOWNS_ANALYTICS,
  DEFAULT_DATE_RANGE,
  FINANCE_ANALYTICS,
  FINANCE_BREAKDOWNS_ANALYTICS,
  GENERIC_ANALYTICS,
  getWholeRange,
  isDateStringHourly,
  SCI_PLAY_ANALYTICS_ONLY,
  SCIPLAY_ANALYTICS_ONLY,
  SCIPLAY_BREAKDOWNS_ANALYTICS
} from '../utils';

// todo: are we on the finance tab? we need to make sure
// that we only query for finance analytics if we are
export function useAnalyticsData(
  apiDateRange: DateRange | undefined,
  apiPreviousDateRange: DateRange | undefined,
  publisherId: string | undefined,
  shouldDisplaySciPlayAnalytics: boolean,
  attributeFilters?: Record<string, string[]>
) {


  const analyticsToQuery = shouldDisplaySciPlayAnalytics
    ? SCIPLAY_ANALYTICS_ONLY
    : GENERIC_ANALYTICS;

  const breakdownsToQuery = shouldDisplaySciPlayAnalytics
    ? SCIPLAY_BREAKDOWNS_ANALYTICS
    : [...BUSINESS_BREAKDOWNS_ANALYTICS, ...FINANCE_BREAKDOWNS_ANALYTICS];

  const analytics = useAnalytics(
    apiDateRange?.from?.toISOString() ??
      getWholeRange(DEFAULT_DATE_RANGE)!.from!.toISOString(),
    apiDateRange?.to?.toISOString() ??
      getWholeRange(DEFAULT_DATE_RANGE)!.to!.toISOString(),
    analyticsToQuery,
    analyticsToQuery,
    breakdownsToQuery,
    publisherId,
    attributeFilters
  );

  const previousAnalytics = useAnalytics(
    apiPreviousDateRange?.from?.toISOString(),
    apiPreviousDateRange?.to?.toISOString(),
    analyticsToQuery,
    analyticsToQuery,
    breakdownsToQuery,
    publisherId,
    attributeFilters
  );

  const isLoading = useMemo(() => {
    return (
      analytics.getAnalytics.isLoading ||
      analytics.getTrends.isLoading ||
      previousAnalytics.getAnalytics.isLoading ||
      previousAnalytics.getTrends.isLoading
    );
  }, [analytics, previousAnalytics]);

  const filteredAnalytics = useMemo(() => {
    const firstTrendDate = analytics.getTrends?.data?.[0]?.values?.[0]?.date;

    return ANALYTICS.filter((metricName) => {
      if (metricName === EMetric.TOTAL_REVENUE) return false;

      return !(
        firstTrendDate &&
        isDateStringHourly(firstTrendDate) &&
        metricName === EMetric.DAU
      );
    }).reduce(
      (
        acc: { businessAnalytics: EMetric[]; financeAnalytics: EMetric[] },
        metricName
      ) => {
        if (BUSINESS_ANALYTICS.includes(metricName)) {
          acc.businessAnalytics.push(metricName);
        } else {
          if (
            shouldDisplaySciPlayAnalytics &&
            SCI_PLAY_ANALYTICS_ONLY.includes(metricName)
          ) {
            acc.financeAnalytics.push(metricName);
          } else if (
            !shouldDisplaySciPlayAnalytics &&
            FINANCE_ANALYTICS.includes(metricName)
          ) {
            acc.financeAnalytics.push(metricName);
          }
        }
        return acc;
      },
      { businessAnalytics: [], financeAnalytics: [] }
    );
  }, [analytics.getTrends?.data]);

  return {
    filteredAnalytics,
    analytics,
    previousAnalytics,
    isLoading
  };
}
